import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import MainLayout from "@/components/MainLayout";
import HomePageNew from "@/pages/HomePageNew";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import AdminPage from "@/pages/AdminPage";
import CartPage from "@/pages/CartPage";
import OrdersPage from "@/pages/OrdersPage";
import StatsPage from "@/pages/StatsPage";
import ReconciliationPage from "@/pages/ReconciliationPage";
import SharePage from "@/pages/SharePage";
import ChangePasswordPage from "@/pages/ChangePasswordPage";
import CrmClientsPage from "@/pages/CrmClientsPage";
import CrmClientDetailPage from "@/pages/CrmClientDetailPage";
import CrmSettingsPage from "@/pages/CrmSettingsPage";
import CrmCustomerOrdersPage from "@/pages/CrmCustomerOrdersPage";
import CrmStockWatchesPage from "@/pages/CrmStockWatchesPage";
import CrmMarketingPage from "@/pages/CrmMarketingPage";
import CrmCustomerOrderPublicPage from "@/pages/CrmCustomerOrderPublicPage";
import PublicPageErrorBoundary from "@/components/PublicPageErrorBoundary";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { WebSocketProvider } from "@/context/WebSocketContext";

const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (adminOnly && !user.is_admin) {
    return <Navigate to="/" replace />;
  }

  return children;
};

const CatalogPage = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (user?.is_admin) {
    return (
      <MainLayout fullscreen crmAdminOnly>
        <HomePageNew embedded />
      </MainLayout>
    );
  }

  return <HomePageNew />;
};

function AppContent() {
  return (
    <div className="App">
      <Routes>
        <Route path="/share" element={<SharePage />} />
        <Route
          path="/public/customer-orders/:publicToken"
          element={
            <PublicPageErrorBoundary>
              <CrmCustomerOrderPublicPage />
            </PublicPageErrorBoundary>
          }
        />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/" element={<CatalogPage />} />
        <Route
          path="/cart"
          element={
            <ProtectedRoute>
              <MainLayout>
                <CartPage />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin"
          element={
            <ProtectedRoute adminOnly>
              <MainLayout>
                <AdminPage />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/orders"
          element={
            <ProtectedRoute>
              <OrdersPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/stats"
          element={
            <ProtectedRoute adminOnly>
              <MainLayout>
                <StatsPage />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/reconciliation"
          element={
            <ProtectedRoute adminOnly>
              <ReconciliationPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/change-password"
          element={
            <ProtectedRoute>
              <MainLayout>
                <ChangePasswordPage />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/crm/clients"
          element={
            <ProtectedRoute>
              <PublicPageErrorBoundary>
                <MainLayout>
                  <CrmClientsPage />
                </MainLayout>
              </PublicPageErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/crm/clients/:clientId"
          element={
            <ProtectedRoute>
              <PublicPageErrorBoundary>
                <MainLayout>
                  <CrmClientDetailPage />
                </MainLayout>
              </PublicPageErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/crm/settings"
          element={
            <ProtectedRoute>
              <MainLayout>
                <CrmSettingsPage />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/crm/stock-watches"
          element={
            <ProtectedRoute>
              <PublicPageErrorBoundary>
                <MainLayout>
                  <CrmStockWatchesPage />
                </MainLayout>
              </PublicPageErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/crm/marketing"
          element={
            <ProtectedRoute>
              <PublicPageErrorBoundary>
                <MainLayout wide>
                  <CrmMarketingPage />
                </MainLayout>
              </PublicPageErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/crm/customer-orders"
          element={
            <ProtectedRoute>
              <MainLayout>
                <CrmCustomerOrdersPage />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/crm/customer-orders/new"
          element={
            <ProtectedRoute>
              <MainLayout>
                <CrmCustomerOrdersPage />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/crm/customer-orders/:orderId/edit"
          element={
            <ProtectedRoute>
              <MainLayout>
                <CrmCustomerOrdersPage />
              </MainLayout>
            </ProtectedRoute>
          }
        />
      </Routes>
      <Toaster position="bottom-right" invert gap={12} />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <WebSocketProvider>
          <AppContent />
        </WebSocketProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
